import { TextInput } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { getDateQuestionValidators } from '@app/domain/form/form.helpers'
import type { Question } from '@app/domain/instrument'

import { FormGroup } from '../form-inputs'

interface Props {
  key: number
  onChange: (value: string) => void
  question: Question
  value: string
}

const FormGroupHelperTextInvalid = styled.label`
  color: #a30000;
`

const DateField = styled(TextInput)`
  padding: 8px 16px;
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: 5px;
  font-size: 16px;
  min-width: 220px;
  max-width: 100%;
  appearance: auto;
`

export const DateQuestion = ({ key, onChange, question, value }: Props) => {
  const [isValidDate, setIsValidDate] = useState<boolean>(true)
  const [stringValue, setStringValue] = useState<string>(value ?? '')
  const [displayErrorMessage, setDisplayErrorMessage] = useState('')

  const validators = useMemo(() => {
    return getDateQuestionValidators(question)
  }, [question])

  const handleBlur = useCallback(() => {
    if (isValidDate) {
      onChange(stringValue)
    } else {
      onChange('')
    }
  }, [isValidDate, onChange, stringValue])

  const handleChange = useCallback(
    (updatedStringValue: string) => {
      const currentDate = new Date()
      setStringValue(updatedStringValue)
      let dateIsValid = true
      let errorMessage = ''

      if (updatedStringValue !== undefined) {
        const errorMessages = validators
          .map((validator) => validator(updatedStringValue, currentDate))
          .filter((errorMessage) => errorMessage.trim() !== '')

        dateIsValid = errorMessages.length === 0
        errorMessage = errorMessages.join(', ')
      }

      setIsValidDate(dateIsValid)
      setDisplayErrorMessage(errorMessage)

      if (dateIsValid) {
        onChange(updatedStringValue)
      } else {
        onChange('')
      }
    },
    [onChange, validators],
  )

  return (
    <FormGroup
      key={`question-${key}`}
      fieldId={question.id}
      label={question.label}
      details={question.details}
    >
      <DateField
        isRequired
        type="date"
        id={question.id}
        name={question.id}
        value={stringValue}
        onChange={handleChange}
        onBlurCapture={handleBlur}
        required={question.required || !isValidDate}
      />
      <FormGroupHelperTextInvalid>
        {displayErrorMessage}
      </FormGroupHelperTextInvalid>
    </FormGroup>
  )
}
